import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = '';
const LIMIT = 50;

const BTN = (active, danger) => ({
  padding: '0.4rem 0.875rem',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontWeight: active ? 'bold' : 'normal',
  border: `1px solid ${danger ? (active ? '#f87171' : 'rgba(248,113,113,0.3)') : (active ? '#a855f7' : 'rgba(168,85,247,0.3)')}`,
  background: danger ? (active ? 'rgba(239,68,68,0.25)' : 'transparent') : (active ? 'rgba(168,85,247,0.5)' : 'rgba(168,85,247,0.1)'),
  color: '#fff',
  transition: 'all 0.15s',
});

function BotLogsTab({ adminToken }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [botType, setBotType] = useState('');
  const [onlyNegative, setOnlyNegative] = useState(false);
  const [dateRange, setDateRange] = useState('all');   // 'all' | 'week' | 'day'
  const [offset, setOffset] = useState(0);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // Config editor
  const [configType, setConfigType] = useState('sales');
  const [configText, setConfigText] = useState('');
  const [configSource, setConfigSource] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaved, setConfigSaved] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  // ── helpers ──────────────────────────────────────────────────
  const buildParams = useCallback((extraLimit) => {
    const p = new URLSearchParams({
      admin_token: adminToken,
      limit: extraLimit ?? LIMIT,
      offset: extraLimit ? 0 : offset,
    });
    if (botType) p.set('bot_type', botType);
    if (onlyNegative) p.set('only_negative', 'true');
    if (dateRange !== 'all') p.set('date_range', dateRange);
    return p;
  }, [adminToken, botType, onlyNegative, dateRange, offset]);

  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll  = () => setExpandedIds(new Set(logs.map(l => l.id)));
  const collapseAll = () => setExpandedIds(new Set());

  // ── load logs ────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/chat-logs?${buildParams()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setLogs(d.logs || []);
      setTotal(d.total || 0);
      setExpandedIds(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // ── export CSV ───────────────────────────────────────────────
  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/chat-logs?${buildParams(10000)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      const rows = d.logs || [];
      const flat = v => String(v ?? '').replace(/\r?\n+/g, ' | ').trim();
      const esc  = v => `"${flat(v).replace(/"/g, '""')}"`;
      const fmtDate = s => {
        const d = new Date(s);
        return isNaN(d) ? s : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      };
      const fmtBot  = t => t === 'sales' ? 'Sales-Bot' : 'Support-Bot';
      const fmtFlag = n => n === 1 ? 'Negativ' : 'Positiv';
      const header = 'Nr;Bot;Datum;Bewertung;Frage (Nutzer);Antwort (Bot)';
      const csv = [header, ...rows.map(r =>
        [r.id, fmtBot(r.bot_type), fmtDate(r.created_at), fmtFlag(r.is_negative), esc(r.user_message), esc(r.bot_reply)].join(';')
      )].join('\n');
      const label = dateRange === 'day' ? '1tag' : dateRange === 'week' ? '1woche' : 'alle';
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timelock-bot-logs-${label}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export fehlgeschlagen: ' + e.message); }
    finally { setExporting(false); }
  };

  // ── config editor ────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    setConfigLoading(true); setConfigSaved('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/bot-config/${configType}?admin_token=${adminToken}`);
      if (res.ok) {
        const d = await res.json();
        setConfigText(d.system_prompt || '');
        setConfigSource(d.source || '');
      } else { setConfigText(''); setConfigSource(''); }
    } catch { setConfigText(''); setConfigSource(''); }
    finally { setConfigLoading(false); }
  }, [adminToken, configType]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setConfigLoading(true); setConfigSaved('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/bot-config/${configType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_token: adminToken, system_prompt: configText }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setConfigSaved('✅ Gespeichert! Aktiv bei nächster Bot-Anfrage.');
    } catch (e) { setConfigSaved(`❌ Fehler: ${e.message}`); }
    finally { setConfigLoading(false); }
  };

  const testPrompt = async () => {
    if (!testInput.trim()) return;
    setTestLoading(true); setTestResult('');
    try {
      const ep = configType === 'support' ? '/api/chat/support' : '/api/chat/sales';
      const res = await fetch(`${API_BASE}${ep}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: testInput }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setTestResult(d.reply || '(Keine Antwort)');
    } catch (e) { setTestResult(`Fehler: ${e.message}`); }
    finally { setTestLoading(false); }
  };

  const downloadPrompt = () => {
    if (!configText) return;
    const blob = new Blob([configText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timelock-${configType}-prompt.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const negCount = logs.filter(l => l.is_negative).length;

  // ── render ───────────────────────────────────────────────────
  return (
    <div style={{ color: '#fff' }}>

      {/* ===== CHAT LOGS ===== */}
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.25rem' }}>🤖 Bot-Gespräche</h3>

      {/* Filter row 1 — Bot type + Negativ */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <select
          value={botType}
          onChange={e => { setBotType(e.target.value); setOffset(0); }}
          style={{ background: '#1e1b4b', color: '#fff', border: '1px solid #a855f7', borderRadius: '0.375rem', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem' }}
        >
          <option value="">Alle Bots</option>
          <option value="support">🆘 Support-Bot</option>
          <option value="sales">🛒 Sales-Bot</option>
        </select>

        <button onClick={() => { setOnlyNegative(v => !v); setOffset(0); }} style={BTN(onlyNegative, true)}>
          🔴 Nur negative
        </button>

        <span style={{ color: '#475569', fontSize: '0.75rem' }}>|</span>

        {/* Date range */}
        {[['all','Alle'],['week','1 Woche'],['day','1 Tag']].map(([v, label]) => (
          <button key={v} onClick={() => { setDateRange(v); setOffset(0); }} style={BTN(dateRange === v, false)}>
            {label}
          </button>
        ))}

        <span style={{ color: '#475569', fontSize: '0.75rem' }}>|</span>

        <button onClick={() => { setOffset(0); loadLogs(); }} style={{ ...BTN(false, false), background: 'rgba(168,85,247,0.25)' }}>
          🔄
        </button>

        {/* Expand/Collapse all */}
        <button onClick={expandAll} style={BTN(false, false)} title="Alle aufklappen">⬇ Alle</button>
        <button onClick={collapseAll} style={BTN(false, false)} title="Alle einklappen">⬆ Alle</button>

        {/* Export */}
        <button
          onClick={exportCSV}
          disabled={exporting}
          title="Aktuelle Filtereinstellungen als CSV exportieren"
          style={{ ...BTN(false, false), marginLeft: 'auto', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', color: '#6ee7b7' }}
        >
          {exporting ? '⏳ Exportiere…' : '📤 CSV Export'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ color: '#475569', fontSize: '0.75rem', marginBottom: '1rem' }}>
        {total} Gespräche{negCount > 0 && ` · ${negCount} negativ (diese Seite)`}
        {dateRange !== 'all' && ` · Zeitraum: ${dateRange === 'day' ? 'Heute' : 'Letzte 7 Tage'}`}
      </div>

      {loading && <p style={{ color: '#94a3b8' }}>Lade…</p>}
      {error && <p style={{ color: '#f87171' }}>Fehler: {error}</p>}

      {/* Log entries — collapsible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.5rem' }}>
        {logs.length === 0 && !loading && (
          <p style={{ color: '#64748b', fontStyle: 'italic' }}>Keine Gespräche für diesen Filter.</p>
        )}
        {logs.map(log => {
          const expanded = expandedIds.has(log.id);
          return (
            <div
              key={log.id}
              style={{
                background: log.is_negative ? 'rgba(239,68,68,0.08)' : 'rgba(30,27,75,0.5)',
                border: `1px solid ${log.is_negative ? 'rgba(239,68,68,0.35)' : 'rgba(168,85,247,0.18)'}`,
                borderRadius: '0.5rem',
                overflow: 'hidden',
              }}
            >
              {/* Header row — always visible, click to expand */}
              <div
                onClick={() => toggleExpanded(log.id)}
                style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.6rem 0.875rem', cursor: 'pointer', userSelect: 'none' }}
              >
                <span style={{ fontSize: '0.65rem', background: log.bot_type === 'sales' ? 'rgba(168,85,247,0.3)' : 'rgba(59,130,246,0.3)', padding: '0.1rem 0.45rem', borderRadius: '9999px', whiteSpace: 'nowrap' }}>
                  {log.bot_type === 'sales' ? '🛒' : '🆘'}
                </span>
                {log.is_negative === 1 && <span style={{ color: '#f87171', fontSize: '0.7rem' }}>🔴</span>}
                <span style={{ flex: 1, fontSize: '0.85rem', color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.user_message}
                </span>
                <span style={{ color: '#475569', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                  {new Date(log.created_at).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                </span>
                <span style={{ color: '#64748b', fontSize: '0.7rem', marginLeft: '0.25rem' }}>{expanded ? '▲' : '▼'}</span>
              </div>

              {/* Body — only when expanded */}
              {expanded && (
                <div style={{ padding: '0 0.875rem 0.75rem', borderTop: '1px solid rgba(168,85,247,0.12)' }}>
                  <div style={{ paddingTop: '0.6rem', marginBottom: '0.35rem', fontSize: '0.8rem' }}>
                    <span style={{ color: '#94a3b8', fontWeight: 'bold' }}>Frage: </span>
                    <span style={{ color: '#e2e8f0' }}>{log.user_message}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem' }}>
                    <span style={{ color: '#94a3b8', fontWeight: 'bold' }}>Bot: </span>
                    <span style={{ color: log.is_negative ? '#fca5a5' : '#c4b5fd', whiteSpace: 'pre-wrap' }}>{log.bot_reply}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', alignItems: 'center' }}>
          <button onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0} style={{ ...BTN(false, false), opacity: offset === 0 ? 0.4 : 1 }}>← Zurück</button>
          <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{offset + 1}–{Math.min(offset + LIMIT, total)} von {total}</span>
          <button onClick={() => setOffset(offset + LIMIT)} disabled={offset + LIMIT >= total} style={{ ...BTN(false, false), opacity: offset + LIMIT >= total ? 0.4 : 1 }}>Weiter →</button>
        </div>
      )}

      {/* ===== BOT PROMPT EDITOR ===== */}
      <hr style={{ borderColor: 'rgba(168,85,247,0.2)', margin: '2rem 0' }} />
      <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem' }}>⚙️ Bot-Prompt bearbeiten</h3>
      <p style={{ color: '#64748b', fontSize: '0.78rem', marginBottom: '1rem' }}>Änderungen sofort aktiv ohne Neustart. Erst testen, dann speichern.</p>

      <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {[
          { key: 'knowledge', label: '📚 Wissensbasis (geteilt)' },
          { key: 'sales',     label: '🛒 Sales-Bot (Website)' },
          { key: 'support',   label: '🆘 Support-Bot (App)' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setConfigType(key)} style={BTN(configType === key, false)}>{label}</button>
        ))}
      </div>

      {configType === 'knowledge' && (
        <div style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.22)', borderRadius: '0.5rem', padding: '0.6rem 0.8rem', marginBottom: '0.6rem', fontSize: '0.76rem', color: '#86efac' }}>
          📚 Beide Bots nutzen diese Wissensbasis. Hier stehen Fakten zu Paketen, Preisen und FAQ.
        </div>
      )}

      {/* Source badge */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.4rem' }}>
        {configSource === 'db' && <span style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.35)', color: '#4ade80', padding: '0.15rem 0.55rem', borderRadius: '9999px', fontSize: '0.72rem' }}>✏️ DB-Override aktiv</span>}
        {configSource === 'file' && <span style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.25)', color: '#94a3b8', padding: '0.15rem 0.55rem', borderRadius: '9999px', fontSize: '0.72rem' }}>📄 Standard aus Datei</span>}
        {configText && <span style={{ color: '#475569', fontSize: '0.72rem' }}>{configText.length} Zeichen</span>}
      </div>

      <textarea
        value={configText}
        onChange={e => setConfigText(e.target.value)}
        rows={18}
        style={{ width: '100%', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(168,85,247,0.35)', color: '#e2e8f0', borderRadius: '0.5rem', padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.78rem', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.55' }}
        placeholder={configType === 'knowledge' ? 'Wissensbasis laden…' : `Prompt für ${configType === 'sales' ? 'Sales-Bot' : 'Support-Bot'} laden…`}
      />

      {/* Test row */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.65rem', marginBottom: '0.4rem' }}>
        <input
          type="text"
          value={testInput}
          onChange={e => setTestInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && testPrompt()}
          placeholder="Testfrage eingeben und Enter drücken…"
          style={{ flex: 1, background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(168,85,247,0.25)', color: '#fff', borderRadius: '0.375rem', padding: '0.45rem 0.7rem', fontSize: '0.825rem' }}
        />
        <button onClick={testPrompt} disabled={testLoading} style={{ ...BTN(false, false), background: 'rgba(59,130,246,0.25)', border: '1px solid rgba(59,130,246,0.5)', whiteSpace: 'nowrap' }}>
          {testLoading ? '…' : '▶ Testen'}
        </button>
      </div>

      {testResult && (
        <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '0.5rem', padding: '0.7rem', fontSize: '0.825rem', color: '#bfdbfe', whiteSpace: 'pre-wrap', marginBottom: '0.65rem' }}>
          <strong style={{ color: '#93c5fd' }}>Bot-Antwort: </strong>{testResult}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', marginTop: '0.65rem', flexWrap: 'wrap' }}>
        <button onClick={saveConfig} disabled={configLoading} style={{ ...BTN(true, false), padding: '0.55rem 1.25rem', fontWeight: 'bold', opacity: configLoading ? 0.5 : 1 }}>
          {configLoading ? 'Speichere…' : '💾 Speichern (DB-Override)'}
        </button>
        <button onClick={downloadPrompt} disabled={!configText} style={{ ...BTN(false, false), background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', opacity: configText ? 1 : 0.4 }} title="Lokal speichern → in backend/src/prompts/ ablegen → neu bauen">
          📥 Download .txt
        </button>
        <button onClick={loadConfig} style={{ ...BTN(false, false), color: '#64748b' }}>↺ Neu laden</button>
        {configSaved && <span style={{ color: configSaved.startsWith('✅') ? '#4ade80' : '#f87171', fontSize: '0.825rem' }}>{configSaved}</span>}
      </div>

      <div style={{ marginTop: '0.875rem', background: 'rgba(15,23,42,0.35)', border: '1px solid rgba(168,85,247,0.12)', borderRadius: '0.5rem', padding: '0.65rem 0.8rem', fontSize: '0.72rem', color: '#475569', lineHeight: '1.6' }}>
        <strong style={{ color: '#64748b' }}>Workflow: </strong>
        Bearbeiten → Testen → Speichern (sofort aktiv, überlebt Deploys via DB-Volume)
        &nbsp;·&nbsp;Dauerhaft: Download → <code style={{ color: '#c4b5fd' }}>backend/src/prompts/{configType}.txt</code> → neu bauen
      </div>
    </div>
  );
}

export default BotLogsTab;
